<template>
  <div class="dashboard">
    <header class="dashboard-header">
      <div class="header-content">
        <div class="brand-section">
          <h1>Sigdex Admin</h1>
        </div>

        <!-- Desktop Navigation -->
        <nav class="desktop-nav">
          <button
            v-for="tab in tabs"
            :key="tab.key"
            :class="['nav-item', { active: currentTab === tab.key }]"
            @click="currentTab = tab.key"
          >
            {{ tab.label }}
          </button>
        </nav>

        <!-- Mobile Hamburger Button -->
        <button
          class="mobile-menu-btn"
          :class="{ open: isMobileMenuOpen }"
          @click="isMobileMenuOpen = !isMobileMenuOpen"
        >
          <span class="hamburger-line"></span>
          <span class="hamburger-line"></span>
          <span class="hamburger-line"></span>
        </button>

        <div class="user-section">
          <span class="welcome-text">{{ claims?.username || 'Admin' }}</span>
          <button class="logout-btn" @click="handleLogout">Logout</button>
        </div>
      </div>

      <!-- Mobile Navigation Menu -->
      <nav class="mobile-nav" :class="{ open: isMobileMenuOpen }">
        <button
          v-for="tab in tabs"
          :key="`mobile-${tab.key}`"
          :class="['mobile-nav-item', { active: currentTab === tab.key }]"
          @click="selectTab(tab.key)"
        >
          {{ tab.label }}
        </button>
        <div class="mobile-user-section">
          <span class="mobile-welcome">{{ claims?.username || 'Admin' }}</span>
          <button class="mobile-logout-btn" @click="handleLogout">Logout</button>
        </div>
      </nav>
    </header>

    <main class="dashboard-content">
      <!-- Dashboard Notes - appears on all pages -->
      <DashboardNotes :current-page="currentTab" />

      <!-- Overview Tab -->
      <div v-if="currentTab === 'overview'">
        <OverviewDashboard />
      </div>

      <!-- Users Tab -->
      <div v-else-if="currentTab === 'users'">
        <UserDashboard title="All Users" :show-stats="true" />
      </div>

      <!-- Server Tab -->
      <div v-else-if="currentTab === 'server'">
        <ServerDashboard />
      </div>

      <!-- Database Tab -->
      <div v-else-if="currentTab === 'database'">
        <DatabaseDashboard />
      </div>

      <!-- Bug Reports Tab -->
      <div v-else-if="currentTab === 'bug-reports'">
        <BugReportsDashboard />
      </div>

      <!-- Redemption Codes Tab -->
      <div v-else-if="currentTab === 'codes'">
        <CodesDashboard />
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useLocalStorage } from '@vueuse/core';
import { claims } from '../client.js';
import ServerDashboard from './ServerDashboard.vue';
import UserDashboard from './UserDashboard.vue';
import OverviewDashboard from './OverviewDashboard.vue';
import DatabaseDashboard from './DatabaseDashboard.vue';
import BugReportsDashboard from './BugReportsDashboard.vue';
import CodesDashboard from './CodesDashboard.vue';
import DashboardNotes from './DashboardNotes.vue';

const emit = defineEmits<{
  logout: [];
}>();

// Tab configuration
const tabs = [
  { key: 'overview', label: 'Overview' },
  { key: 'users', label: 'Users' },
  { key: 'server', label: 'Server' },
  { key: 'database', label: 'Database' },
  { key: 'bug-reports', label: 'Bug Reports' },
  { key: 'codes', label: 'Codes' },
] as const;

type TabKey = (typeof tabs)[number]['key'];

// Use VueUse's useLocalStorage to persist current tab
const currentTab = useLocalStorage<TabKey>('admin-dashboard-tab', 'overview');

// Mobile menu state
const isMobileMenuOpen = ref(false);

// Helper function to select tab and close mobile menu
const selectTab = (tabKey: TabKey) => {
  currentTab.value = tabKey;
  isMobileMenuOpen.value = false;
};

const handleLogout = () => {
  emit('logout');
};
</script>

<style scoped>
.dashboard {
  min-height: 100vh;
  background: #f9fafb;
}

.dashboard-header {
  background: #1f2937;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 1.5rem;
  height: 64px;
  max-width: 1400px;
  margin: 0 auto;
}

.brand-section h1 {
  margin: 0;
  color: white;
  font-size: 1.5rem;
  font-weight: 700;
}

/* Desktop Navigation */
.desktop-nav {
  display: flex;
  gap: 0.5rem;
}

.nav-item {
  padding: 0.5rem 1rem;
  background: none;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-weight: 500;
  color: #d1d5db;
  transition: all 0.2s ease;
  font-size: 0.875rem;
}

.nav-item:hover {
  color: white;
  background: rgba(255, 255, 255, 0.1);
}

.nav-item.active {
  color: #3b82f6;
  background: rgba(59, 130, 246, 0.1);
}

/* User Section */
.user-section {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.welcome-text {
  color: #d1d5db;
  font-size: 0.875rem;
}

.logout-btn {
  padding: 0.5rem 1rem;
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: background-color 0.2s;
}

.logout-btn:hover {
  background: #dc2626;
}

/* Mobile Menu Button */
.mobile-menu-btn {
  display: none;
  flex-direction: column;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 0.375rem;
  transition: background-color 0.2s;
}

.mobile-menu-btn:hover {
  background: rgba(255, 255, 255, 0.1);
}

.hamburger-line {
  display: block;
  height: 3px;
  width: 24px;
  background: #d1d5db;
  margin: 3px 0;
  transition: all 0.3s ease;
  transform-origin: center;
}

.mobile-menu-btn.open .hamburger-line:nth-child(1) {
  transform: rotate(45deg) translate(6px, 6px);
}

.mobile-menu-btn.open .hamburger-line:nth-child(2) {
  opacity: 0;
}

.mobile-menu-btn.open .hamburger-line:nth-child(3) {
  transform: rotate(-45deg) translate(6px, -6px);
}

/* Mobile Navigation */
.mobile-nav {
  display: none;
  flex-direction: column;
  background: #374151;
  border-top: 1px solid #4b5563;
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease;
}

.mobile-nav.open {
  max-height: 400px;
}

.mobile-nav-item {
  width: 100%;
  padding: 1rem 1.5rem;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  font-weight: 500;
  color: #d1d5db;
  transition: all 0.2s ease;
  border-bottom: 1px solid #4b5563;
}

.mobile-nav-item:hover {
  color: white;
  background: rgba(255, 255, 255, 0.05);
}

.mobile-nav-item.active {
  color: #3b82f6;
  background: rgba(59, 130, 246, 0.1);
}

.mobile-user-section {
  padding: 1.5rem;
  border-top: 1px solid #4b5563;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
}

.mobile-welcome {
  color: #d1d5db;
  font-size: 0.875rem;
}

.mobile-logout-btn {
  padding: 0.5rem 1.5rem;
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: background-color 0.2s;
}

.mobile-logout-btn:hover {
  background: #dc2626;
}

/* Dashboard Content */
.dashboard-content {
  padding: 1.5rem;
  max-width: 1400px;
  margin: 0 auto;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .header-content {
    padding: 0 1rem;
  }

  .brand-section h1 {
    font-size: 1.25rem;
  }

  /* Hide desktop nav on mobile */
  .desktop-nav,
  .user-section {
    display: none;
  }

  /* Show mobile menu button */
  .mobile-menu-btn {
    display: flex;
  }

  /* Show mobile nav */
  .mobile-nav {
    display: flex;
  }

  .dashboard-content {
    padding: 1rem;
  }
}

@media (max-width: 480px) {
  .header-content {
    padding: 0 0.75rem;
  }

  .brand-section h1 {
    font-size: 1.125rem;
  }

  .dashboard-content {
    padding: 0.75rem;
  }
}
</style>
